/**
 * AuthContext — gerenciamento de sessão JWT.
 *
 * O token JWT é armazenado em sessionStorage (limpo ao fechar a aba).
 * Em produção, prefira cookie httpOnly com refresh token para maior segurança.
 *
 * O claim `plan` é incluído no JWT desde a Fase 5. O claim `role` determina
 * quais rotas são acessíveis (ver PrivateRoute).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type { TokenClaims } from '../types/api'

const STORAGE_KEY = 'pontoflow_token'

function parseJwt(token: string): TokenClaims {
  const [, payload] = token.split('.')
  const padded = payload.replace(/-/g, '+').replace(/_/g, '/')
  return JSON.parse(atob(padded)) as TokenClaims
}

function isExpired(claims: TokenClaims): boolean {
  return Date.now() / 1000 >= claims.exp
}

// ── Contexto ──────────────────────────────────────────────────────────────────

interface AuthState {
  token: string | null
  user: TokenClaims | null
}

interface AuthContextValue extends AuthState {
  isAuthenticated: boolean
  login: (token: string) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

// ── Provider ──────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (!stored) return { token: null, user: null }
    try {
      const claims = parseJwt(stored)
      if (isExpired(claims)) {
        sessionStorage.removeItem(STORAGE_KEY)
        return { token: null, user: null }
      }
      return { token: stored, user: claims }
    } catch {
      sessionStorage.removeItem(STORAGE_KEY)
      return { token: null, user: null }
    }
  })

  // Verifica expiração periodicamente.
  useEffect(() => {
    if (!state.user) return
    const ms = state.user.exp * 1000 - Date.now()
    if (ms <= 0) {
      logout()
      return
    }
    const timer = setTimeout(() => logout(), Math.min(ms, 2_147_483_647))
    return () => clearTimeout(timer)
  }, [state.user])

  const login = useCallback((token: string) => {
    try {
      const claims = parseJwt(token)
      sessionStorage.setItem(STORAGE_KEY, token)
      setState({ token, user: claims })
    } catch {
      console.error('Token JWT inválido')
    }
  }, [])

  const logout = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY)
    setState({ token: null, user: null })
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, isAuthenticated: !!state.user, login, logout }),
    [state, login, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>')
  return ctx
}
